import React, { useContext } from 'react';
import { AppTableContext } from '../AppTableContext';
import { WMButton, WMButtonVariant, WMDivider } from '@walkme/wm-ui';
import clsx from 'clsx';

import { ReactComponent as XIcon } from '../../../../assets/icons/x-icon.svg';
import { AppTableRowAnimateType, IAppTableRowData, IAppTableRowMultipleAction } from '../model';

import classes from './AppTableActions.module.scss';
import Restricted from '../../../infrastructure/Permissions/Restricted';
import { getFormatNumberString } from '../../../helpers/getFormatNumberString';
import { withPermissionTooltip } from '../../../infrastructure/Permissions/PermissionsProvider';
import { useTranslation } from 'react-i18next';

const keyPrefix = 'components.appTable.appTableActions';

const AppTableActions = ({ disabled }: $TSFixMe) => {
    const { t } = useTranslation('', { keyPrefix });
    const appTableContext = useContext(AppTableContext);

    const { data, selectedIds, addProcessingRowIds, removeProcessingRowIds, multipleActions, tableDataTestid, onTableDataChange } = appTableContext;

    const checkedRows: IAppTableRowData[] = data.filter(n => selectedIds.has(n.id));

    const closeActions = () => {
        onTableDataChange?.({ selectedIds: [] });
    };

    const getCheckedRows = (): IAppTableRowMultipleAction[] | undefined => {
        return multipleActions?.filter(action => !action?.isHidden?.(checkedRows));
    };

    const getAllRestrictedToValues = () => {
        return getCheckedRows()?.reduce((unique, multipleAction) => {
            unique =
                multipleAction?.restrictedTo?.[0] && !unique.includes(multipleAction?.restrictedTo?.[0])
                    ? [...unique, ...multipleAction?.restrictedTo]
                    : unique;
            return unique;
        }, [] as string[]);
    };

    const getCreatorsIds = () => {
        const creatorsIds: number[] = data.filter(selected => selectedIds.has(selected.id) && selected.user?.id).map(selected => selected.user!.id);

        return creatorsIds.filter((item, index) => creatorsIds.indexOf(item) === index);
    };

    return (
        <>
            <Restricted to={getAllRestrictedToValues()} creatorsIds={getCreatorsIds()}>
                <div className={classes.root} data-testid={`${tableDataTestid}-AppTableActions`}>
                    <div className={classes.selectAllWrapper}>
                        <span className={classes.selectAllText}>
                            <span className={classes.selectAllNum}>{getFormatNumberString(checkedRows.length)}</span>
                            {t('itemsSelected')}
                        </span>
                    </div>

                    <div className={classes.multipleActionsContainer}>
                        {getCheckedRows()?.map((multipleAction, index) => {
                            const disabledByPermissions = multipleAction.isDisabled?.(checkedRows);
                            const el = (
                                <WMButton
                                    key={index}
                                    className={clsx(classes.actionButton, multipleAction.className)}
                                    disabled={multipleAction.isDisabled?.(checkedRows) || disabled}
                                    variant={WMButtonVariant.Text}
                                    iconComponent={multipleAction.icon && <multipleAction.icon className={classes.actionButtonIcon} />}
                                    data-testid={`${tableDataTestid}-${multipleAction.name || multipleAction.label}`}
                                    onClick={async () => {
                                        if (multipleAction.handler) {
                                            const ids = checkedRows.map(row => row.id);
                                            addProcessingRowIds?.(ids, AppTableRowAnimateType.active);
                                            try {
                                                await multipleAction.handler(checkedRows, appTableContext);
                                            } finally {
                                                closeActions();
                                                removeProcessingRowIds?.(ids, AppTableRowAnimateType.active);
                                            }
                                        }
                                    }}
                                >
                                    {multipleAction.label}
                                </WMButton>
                            );
                            return withPermissionTooltip(el, !disabledByPermissions);
                        })}

                        <div className={classes.closeArea}>
                            <WMDivider className={classes.divider} />

                            <WMButton
                                disabled={disabled}
                                className={classes.closeButton}
                                variant={WMButtonVariant.Text}
                                onClick={closeActions}
                                data-testid={`${tableDataTestid}-close-actions`}
                            >
                                <XIcon fontSize={24} />
                            </WMButton>
                        </div>
                    </div>
                </div>
            </Restricted>
        </>
    );
};

export default AppTableActions;
